import {
  format,
  addMonths,
  endOfMonth,
  getISODay,
  subDays,
  parse,
} from 'date-fns'
import { formatLocal } from 'utils/date'
import { UserProps } from 'context/user/user.types'
import { translate } from 'locales'
import {
  IFinancialInvoiceRow,
  IFinancialMonthDetail,
  MonthlyProductionStatus,
} from './types'

const PSYCHOLOGY_SLUGS = ['psicologo', 'psicologia', 'psicólogo']
const NUTRITION_SLUGS = ['nutricionista', 'nutrição', 'nutricao']
const GENERAL_PRACTITIONER_SLUGS = [
  'clinico-geral',
  'clinico geral',
  'clínico geral',
]

const SPECIALTY_SLUG_LABELS: Record<string, string> = {
  psicologo: 'Psicologia',
  psicóloga: 'Psicologia',
  psicólogo: 'Psicologia',
  psicologia: 'Psicologia',
  nutricionista: 'Nutrição',
  nutricao: 'Nutrição',
  nutrição: 'Nutrição',
  'clinico-geral': 'Clínico Geral',
  'clinico geral': 'Clínico Geral',
  'clínico geral': 'Clínico Geral',
}

export const getSpecialtyLabel = (
  specialtyName?: string | null,
  specialtySlug?: string | null
): string => {
  const name = specialtyName?.trim() ?? ''
  const slug = specialtySlug?.trim() ?? ''
  const mappedFromName = SPECIALTY_SLUG_LABELS[name.toLowerCase()]

  if (mappedFromName) return mappedFromName
  if (name) return name

  const mappedFromSlug = SPECIALTY_SLUG_LABELS[slug.toLowerCase()]

  return mappedFromSlug || slug
}

export const isPsychologyDoctor = (userData: UserProps): boolean => {
  const specialtySlug = userData.profile?.specialty?.toLowerCase() ?? ''

  return (
    PSYCHOLOGY_SLUGS.some((slug) => specialtySlug.includes(slug)) ||
    (userData.specialties?.some((specialty) => {
      const slug = String(
        (specialty as { slug?: string }).slug ?? ''
      ).toLowerCase()
      const name = String(specialty.name ?? '').toLowerCase()

      return (
        PSYCHOLOGY_SLUGS.includes(slug) ||
        name.includes('psicolog') ||
        name.includes('psicólog')
      )
    }) ??
      false)
  )
}

export const isNutritionistDoctor = (userData: UserProps): boolean => {
  const specialtySlug = userData.profile?.specialty?.toLowerCase() ?? ''

  return (
    NUTRITION_SLUGS.some((slug) => specialtySlug.includes(slug)) ||
    (userData.specialties?.some((specialty) => {
      const slug = String(
        (specialty as { slug?: string }).slug ?? ''
      ).toLowerCase()
      const name = String(specialty.name ?? '').toLowerCase()

      return NUTRITION_SLUGS.includes(slug) || name.includes('nutri')
    }) ??
      false)
  )
}

export const isGeneralPractitionerDoctor = (userData: UserProps): boolean => {
  const specialtySlug = userData.profile?.specialty?.toLowerCase() ?? ''

  return (
    GENERAL_PRACTITIONER_SLUGS.some((slug) => specialtySlug.includes(slug)) ||
    (userData.specialties?.some((specialty) => {
      const slug = String(
        (specialty as { slug?: string }).slug ?? ''
      ).toLowerCase()
      const name = String(specialty.name ?? '').toLowerCase()

      return (
        GENERAL_PRACTITIONER_SLUGS.includes(slug) ||
        name.includes('clinico geral') ||
        name.includes('clínico geral')
      )
    }) ??
      false)
  )
}

export const formatDocument = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11)

  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }

  return digits
}

export const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11)

  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
  }

  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  }

  return digits
}

export const onlyDigits = (value: string): string => value.replace(/\D/g, '')

export const onlyAlphanumeric = (value: string): string =>
  value.toUpperCase().replace(/[^0-9A-Z]/g, '')

export const formatCnpj = (value: string): string => {
  const cleaned = onlyAlphanumeric(value).slice(0, 14)

  if (cleaned.length === 14) {
    return cleaned.replace(/(.{2})(.{3})(.{3})(.{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }

  return cleaned
}

export const formatCpfOrCnpj = (value: string): string => {
  const cleaned = onlyAlphanumeric(value).slice(0, 14)

  if (cleaned.length === 11 && /^\d{11}$/.test(cleaned)) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }

  if (cleaned.length === 14) {
    return cleaned.replace(/(.{2})(.{3})(.{3})(.{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }

  return cleaned
}

export const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

export const isValidPhone = (value: string): boolean => {
  const digits = onlyDigits(value)
  return digits.length === 10 || digits.length === 11
}

const isValidCPFDigits = (digits: string): boolean => {
  if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) return false

  const calcCheckDigit = (length: number): number => {
    let sum = 0
    for (let i = 0; i < length; i++) {
      sum += parseInt(digits[i], 10) * (length + 1 - i)
    }
    const remainder = (sum * 10) % 11
    return remainder === 10 ? 0 : remainder
  }

  return (
    calcCheckDigit(9) === parseInt(digits[9], 10) &&
    calcCheckDigit(10) === parseInt(digits[10], 10)
  )
}

const cnpjCharValue = (char: string): number => char.charCodeAt(0) - 48

const isValidCNPJChars = (chars: string): boolean => {
  if (chars.length !== 14 || /^(.)\1{13}$/.test(chars)) return false
  if (!/^\d{2}$/.test(chars.slice(12))) return false

  const calcCheckDigit = (length: number): number => {
    const weights =
      length === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
    let sum = 0
    for (let i = 0; i < length; i++) {
      sum += cnpjCharValue(chars[i]) * weights[i]
    }
    const remainder = sum % 11
    return remainder < 2 ? 0 : 11 - remainder
  }

  return (
    calcCheckDigit(12) === parseInt(chars[12], 10) &&
    calcCheckDigit(13) === parseInt(chars[13], 10)
  )
}

export const isValidCPF = (value: string): boolean =>
  isValidCPFDigits(onlyDigits(value))

export const isValidCNPJ = (value: string): boolean =>
  isValidCNPJChars(onlyAlphanumeric(value))

export const isValidCpfOrCnpj = (value: string): boolean => {
  const cleaned = onlyAlphanumeric(value)

  if (cleaned.length === 11 && /^\d{11}$/.test(cleaned)) {
    return isValidCPFDigits(cleaned)
  }

  if (cleaned.length === 14) return isValidCNPJChars(cleaned)

  return false
}

export const isValidNumericWithCheckDigit = (value: string): boolean =>
  /^\d+(-\d+)?$/.test(value.trim())

export const sanitizeNumericWithCheckDigit = (value: string): string => {
  const cleaned = value.replace(/[^\d-]/g, '')
  const [first, ...rest] = cleaned.split('-')

  return rest.length ? `${first}-${rest.join('')}` : first
}

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  checking: 'financial.bankData.accountTypeChecking',
  savings: 'financial.bankData.accountTypeSavings',
}

export const getAccountTypeLabel = (accountType: string): string => {
  const key = ACCOUNT_TYPE_LABELS[accountType]
  return key ? translate(key) : accountType
}

const PIX_KEY_TYPE_LABELS: Record<string, string> = {
  email: 'financial.bankData.pixKeyTypeEmail',
  cpf_cnpj: 'financial.bankData.pixKeyTypeCpfCnpj',
  phone: 'financial.bankData.pixKeyTypePhone',
  random: 'financial.bankData.pixKeyTypeRandom',
}

export const getPixKeyTypeLabel = (pixKeyType: string): string => {
  const key = PIX_KEY_TYPE_LABELS[pixKeyType]
  return key ? translate(key) : pixKeyType
}

export const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''

  const first = parts[0][0]
  const last = parts[parts.length - 1][0]

  return `${first}${last}`.toUpperCase()
}

export const isStartPlan = (plan: string | null): boolean =>
  Boolean(plan?.toLowerCase().includes('start'))

export const formatCurrency = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number.isFinite(value) ? value : 0)

export const formatDate = (value: string | null): string =>
  value ? formatLocal(value, 'dd/MM/yyyy') : translate('financial.notAvailable')

export const getClosingDate = (periodMonth: string): string =>
  format(endOfMonth(parse(periodMonth, 'yyyy-MM', new Date())), 'yyyy-MM-dd')

export const getInvoiceDeadline = (periodMonth: string): string => {
  let lastDay = endOfMonth(
    addMonths(parse(periodMonth, 'yyyy-MM', new Date()), 1)
  )
  while (getISODay(lastDay) > 5) {
    lastDay = subDays(lastDay, 1)
  }
  return format(lastDay, 'yyyy-MM-dd')
}

export const getPaymentMonth = (periodMonth: string): string =>
  format(addMonths(parse(periodMonth, 'yyyy-MM', new Date()), 2), 'yyyy-MM')

export const isMonthClosed = (periodMonth: string): boolean =>
  new Date() >= new Date(getClosingDate(periodMonth))

const FIRST_YEAR_WITH_DATA = 2026
const FIRST_YEAR_START_MONTH = 4

export const sumConsultations = (
  stats: IFinancialMonthDetail['stats']
): number =>
  stats.finished30 +
  stats.finished60 +
  stats.noShow30 +
  stats.noShow60 +
  stats.canceledUnder30_30 +
  stats.canceledUnder30_60

export const getMonthHistoryStatus = (
  detail: IFinancialMonthDetail
): MonthlyProductionStatus => {
  if (!detail.isClosed) return 'current'

  return detail.paymentMonth &&
    new Date(`${detail.paymentMonth}-01`) <= new Date()
    ? 'paid'
    : 'closed'
}

export const MAX_INVOICE_FILE_SIZE_MB = 10
const ALLOWED_INVOICE_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg']

export const isValidInvoiceFileType = (file: File): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  return ALLOWED_INVOICE_EXTENSIONS.includes(extension)
}

export const isValidInvoiceFileSize = (file: File): boolean =>
  file.size <= MAX_INVOICE_FILE_SIZE_MB * 1024 * 1024

export const getVisibleMonths = (year: number): string[] => {
  const startMonth = year === FIRST_YEAR_WITH_DATA ? FIRST_YEAR_START_MONTH : 1
  const months: string[] = []

  for (let month = startMonth; month <= 12; month++) {
    months.push(`${year}-${String(month).padStart(2, '0')}`)
  }

  return months
}

const getCurrentMonth = (): string => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

export const getOpenInvoicePeriods = (
  closedPeriods: Set<string>,
  year: number
): string[] => {
  const currentMonth = getCurrentMonth()
  const closedList = Array.from(closedPeriods).sort()
  const lastClosedMonth = closedList[closedList.length - 1]

  return getVisibleMonths(year).filter((periodMonth) => {
    if (periodMonth > currentMonth || closedPeriods.has(periodMonth)) {
      return false
    }

    return lastClosedMonth
      ? periodMonth > lastClosedMonth
      : periodMonth === currentMonth
  })
}

export const buildOpenInvoiceRow = (
  periodMonth: string
): IFinancialInvoiceRow => ({
  settlementId: null,
  periodMonth,
  totalValue: 0,
  status: 'pending',
  invoiceFileName: null,
  invoiceFileUrl: null,
})
